import { Button } from '@finn-auto/design-system/atoms/button';
import {
  AlertCircle,
  CalendarDays,
  CalendarPlus,
  CheckCircle2,
  Clock,
  Globe,
  Info,
  Loader2,
  RefreshCw,
  Users,
  Video,
} from 'lucide-react';
import { cn } from '../../../../shared';
import {
  icsDownloadUrl,
  type BookingPage,
  type BookingSlot,
  type ConfirmedBooking,
} from '../../services/interviewBookingApi';
import { fmtLongDay, fmtTime, slotRange, TZ } from './format';

/**
 * Left rail: what the meeting IS. Deliberately shows a panel *count* and never
 * interviewer names — the whole point is that the candidate doesn't know who
 * they'll meet until the invite lands.
 */
export function MeetingDetails({
  page,
  selectedSlot,
  booking,
}: {
  page: BookingPage;
  selectedSlot: BookingSlot | null;
  booking: ConfirmedBooking | null;
}) {
  const chosen = booking ?? selectedSlot;
  const plural = page.panelSize === 1 ? '' : 's';

  return (
    <div className="border-b border-gray-200 bg-gray-50 p-5 md:border-b-0 md:border-r">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{page.stageName}</p>
      <h2 className="mt-1 text-lg font-semibold leading-snug text-gray-900">{page.jobTitle}</h2>

      <dl className="mt-4 space-y-2.5 text-sm text-gray-700">
        <Detail icon={Clock} text={`${page.minutes} minutes`} />
        <Detail icon={Video} text="Google Meet (link sent by email)" />
        <Detail icon={Users} text={`${page.panelSize} interviewer${plural}`} />
        <Detail icon={Globe} text={`${page.timeZone} time`} />
        {chosen ? (
          <Detail
            icon={CalendarDays}
            text={`${fmtLongDay(chosen.start)}, ${fmtTime(chosen.start)}`}
            strong
          />
        ) : null}
      </dl>

      {/*
        TA's sentence where they have written one, and it is a PAIR — a round-robin link offers a
        slot as soon as ONE of the team is free and picks whoever that is, so the panel wording
        below was untrue for every first interview until Sep 2026. The proxy picks which of the
        two applies (`lib/bookingCopy.js`); the fallback here follows `meetingType` for the same
        reason, so an unreadable Library cannot put the claim back.
      */}
      <p className="mt-4 text-xs leading-relaxed text-gray-500">
        {page.copy?.meeting ??
          (page.meetingType === 'panel'
            ? `Times shown are when everyone on the panel is free. You’ll meet ${page.panelSize} interviewers and receive all the details by email.`
            : 'Times shown are when someone from the team is free. You’ll meet one interviewer — your booking confirmation says who, along with everything else you need.')}
      </p>
    </div>
  );
}

function Detail({
  icon: Icon,
  text,
  strong = false,
}: {
  icon: React.ElementType;
  text: string;
  strong?: boolean;
}) {
  return (
    <div className={cn('flex items-start gap-2', strong && 'font-semibold text-gray-900')}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
      <span>{text}</span>
    </div>
  );
}

export function Confirmation({
  booking,
  token,
  minutes,
  selfReschedule = true,
  copy,
  onReschedule,
  onCancel,
  busy,
  error,
}: {
  booking: ConfirmedBooking;
  /** The link token — the .ics download is served per booking, under the same credential. */
  token: string;
  minutes: number;
  /**
   * False for anything booked from the action board — a case or a final round. Those times were
   * agreed with the recruiter against a whole panel's calendars, and this page would regenerate
   * alternatives from the derived link: for a long panel it finds NONE, so Reschedule opened an
   * empty picker (Theresa, Sep 2026). Cancelling stays, because a candidate who cannot attend
   * must always be able to say so.
   */
  selfReschedule?: boolean;
  /** The Library's wording for this page, already narrowed by the proxy. */
  copy?: BookingPage['copy'];
  onReschedule: () => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  return (
    <div>
      <div className="flex items-start gap-2">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Interview booked</h3>
          <p className="mt-1 text-sm text-gray-700">
            {fmtLongDay(booking.start)} · {slotRange(booking)} ({minutes} min, {TZ})
          </p>
          {booking.emailSent === false ? (
            <p className="mt-1 text-xs font-semibold text-red-700">
              We could not send your confirmation email. Your interview IS booked — please save
              these details, and this page, now.
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-500">
              {booking.candidateEmail
                ? `Details sent to ${booking.candidateEmail}. Check your inbox.`
                : (copy?.bookedNote ?? 'The details are on their way by email.')}
            </p>
          )}
        </div>
      </div>

      {booking.meetLink ? (
        <a
          href={booking.meetLink}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 underline"
        >
          <Video className="h-4 w-4" />
          Join with Google Meet
        </a>
      ) : null}

      {/*
        The candidate is deliberately NOT a guest on our calendar event (the panel's roster
        would leak), so nothing appears in their calendar by itself — a recruiter testing as a
        candidate found "no calendar invite" and an empty week (Anna Thiel, Sep 2026). The
        email attaches an .ics, but Gmail shows no invite card for a METHOD:PUBLISH file, so
        this page offers the two explicit paths: Google Calendar in one click, or the same .ics
        for every other calendar app.
      */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="inline-flex items-center gap-1.5 text-gray-500">
          <CalendarPlus className="h-4 w-4" />
          Add to calendar:
        </span>
        {booking.googleCalendarUrl ? (
          <a
            href={booking.googleCalendarUrl}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-blue-700 underline"
          >
            Google Calendar
          </a>
        ) : null}
        <a href={icsDownloadUrl(token)} className="font-medium text-blue-700 underline">
          Apple / Outlook (.ics)
        </a>
      </div>

      {error ? <InlineError message={error} /> : null}

      <div className="mt-5 border-t border-gray-200 pt-3">
        <p className="text-xs text-gray-500">
          {copy?.change ??
            (selfReschedule
              ? 'Need a different time? Changing it here is the only way — the invite itself is locked.'
              : 'Need a different time? Please contact your contact in the FINN team — they will arrange a new time with the panel.')}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {selfReschedule && (
            <Button size="sm" variant="outline" onClick={onReschedule} disabled={busy}>
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Reschedule
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel interview
          </Button>
        </div>
      </div>
    </div>
  );
}

export function InlineError({ message }: { message: string }) {
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

/**
 * A final state, not a failure: cancelled, expired or invite-only.
 *
 * Rendered without a retry button on purpose — the previous version showed
 * "Try again" under "this link can no longer be used", which invites the
 * candidate to hammer a button that cannot ever work.
 */
export function NoticePanel({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
      <p>{message}</p>
    </div>
  );
}

/** Shown immediately after the candidate cancels, in place of the slot picker. */
export function CancelledConfirmation({ reasonGiven }: { reasonGiven: string | null }) {
  return (
    <div>
      <div className="flex items-start gap-2">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Your interview is cancelled</h3>
          <p className="mt-1 text-sm text-gray-700">
            We’ve let the hiring team know and removed the meeting from everyone’s calendar. You’ll
            get a confirmation by email.
          </p>
          {reasonGiven ? (
            <p className="mt-2 text-xs italic text-gray-500">You told us: “{reasonGiven}”</p>
          ) : null}
        </div>
      </div>

      <p className="mt-4 border-t border-gray-200 pt-3 text-xs text-gray-500">
        This link can’t be used to book again. If you cancelled by mistake, please contact your
        contact in the hiring team directly and they’ll send you a new one.
      </p>
    </div>
  );
}

export function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <p>{message}</p>
        <Button size="sm" variant="outline" className="mt-2" onClick={onRetry}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          Try again
        </Button>
      </div>
    </div>
  );
}
